import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import type { IProject } from "types/project.types";
import {
	buildChatPageContext,
	chatCompanyIdOf,
	registerClosePageHint,
	useClosePageHint,
} from "utils/chatPageContext";

/**
 * What the assistant is told about the page a turn is sent from: the
 * `[Current Page: ...]` prefix, and the client of the close whose project
 * page is open (null anywhere else). ChatBar and the assistant page both
 * read it here, so the two cannot drift.
 */
export function useChatPageContext(): {
	pageContext: string;
	closeCompanyId: number | null;
} {
	const { pathname } = useLocation();
	const hint = useClosePageHint();
	return {
		pageContext: buildChatPageContext(pathname, hint),
		closeCompanyId: chatCompanyIdOf(pathname, hint),
	};
}

/**
 * A close's project page tells the assistant it is one, once its project has
 * loaded and the close UI is on for the page (`useCloseProjectAccess`). The
 * hint and the client that travels with it are registered together and
 * cleared together: on leaving, on another project, on a project that has
 * not loaded. A project still on screen from the address before this one is
 * registered under this address and never matches it.
 */
export function useClosePageChatHint(
	project: Pick<IProject, "id" | "company"> | null | undefined,
	isCloseOn: boolean,
): void {
	const { pathname } = useLocation();
	const projectId = project?.id;
	const companyId = project?.company?.id ?? null;
	useEffect(() => {
		if (!isCloseOn || !projectId) return;
		return registerClosePageHint({ pathname, projectId, companyId });
	}, [isCloseOn, projectId, companyId, pathname]);
}
